import { FC, ReactNode, useEffect, useMemo, useRef, useState } from 'react';
import { Tabs } from 'antd';
import { ListFilter } from 'lucide-react';
import {
    TbChartDots2,
    TbChevronDown,
    TbChevronRight,
    TbEdit,
    TbInfoCircle,
    TbPalette,
    TbSettings,
    TbShare,
} from 'react-icons/tb';

import { useAntDesignTheme } from '@/hooks/useAntDesignTheme';
import { useReportComponentAccess } from '@/hooks/useNestedResourceAccess';
import { useReportComponentDefinitions } from '@/hooks/useReportComponentDefinitions';
import { FlexContentRegion, FullHeightFlexColumn } from '@/shared';
import { PlatformForm as Form } from '@/shared/components/forms';
import { useReportComponentStore } from '@/stores';
import { ReportComponent } from '@/types';
import { DataSourceType, ReportComponentConfig } from '@/types/api/modules/business-intelligence';

import Header from './layout/00-Header';
import Overview from './layout/01-Overview';
import Filters from './layout/02-Filters';
import Formatting from './layout/03-Formatting';
import { hasChartFormattingControls } from './layout/03-Formatting/components/ChartFormatting';
import Axes from './layout/04-Axes';
import AdditionalSettings from './layout/05-AdditionalSettings';
import Footer from './layout/06-Footer';
import Share from './layout/07-Share';

interface SettingsProps {
    component: ReportComponent;
    onRequestClose: () => void;
    onSave: () => Promise<void>;
}

type SettingsSectionKey = 'overview' | 'filters' | 'axes' | 'formatting' | 'additionalSettings';

const createInitialOpenSections = (): Record<SettingsSectionKey, boolean> => ({
    overview: true,
    filters: false,
    axes: false,
    formatting: false,
    additionalSettings: false,
});

const initialAvailableSections: Record<SettingsSectionKey, boolean> = {
    overview: true,
    filters: false,
    axes: false,
    formatting: false,
    additionalSettings: false,
};

interface SettingsSectionProps {
    title: string;
    description: string;
    icon: ReactNode;
    sectionKey: SettingsSectionKey;
    open: boolean;
    onToggle: (section: SettingsSectionKey) => void;
    enabled?: boolean;
    first?: boolean;
    children: ReactNode;
}

const SettingsSection: FC<SettingsSectionProps> = ({
    title,
    description,
    icon,
    sectionKey,
    open,
    onToggle,
    enabled = true,
    first = false,
    children,
}) => {
    const { token } = useAntDesignTheme();

    return (
        <section style={{ borderTop: first ? 0 : `1px solid ${token.colorBorder}` }}>
            <button
                type='button'
                disabled={!enabled}
                onClick={() => {
                    onToggle(sectionKey);
                }}
                style={{
                    alignItems: 'flex-start',
                    background: 'transparent',
                    border: 0,
                    color: token.colorText,
                    cursor: enabled ? 'pointer' : 'not-allowed',
                    display: 'flex',
                    fontFamily: token.fontFamily,
                    gap: '0.5rem',
                    opacity: enabled ? 1 : 0.55,
                    padding: '1rem',
                    textAlign: 'left',
                    width: '100%',
                }}
            >
                <span style={{ color: token.colorTextTertiary, marginTop: '0.1rem' }}>
                    {open ? <TbChevronDown /> : <TbChevronRight />}
                </span>
                <span style={{ color: token.colorTextTertiary, marginTop: '0.1rem' }}>{icon}</span>
                <span>
                    <strong style={{ color: enabled ? token.colorText : token.colorTextTertiary }}>{title}</strong>
                    <small
                        style={{
                            color: token.colorTextTertiary,
                            display: 'block',
                            marginTop: '0.25rem',
                        }}
                    >
                        {description}
                    </small>
                </span>
            </button>
            {open && enabled && <div style={{ padding: '0 1rem 1rem 3.25rem' }}>{children}</div>}
        </section>
    );
};

const Settings: FC<SettingsProps> = ({ component, onRequestClose, onSave }): JSX.Element => {
    const [openSections, setOpenSections] = useState<Record<SettingsSectionKey, boolean>>(createInitialOpenSections);
    const availableSectionsRef = useRef<Record<SettingsSectionKey, boolean>>(initialAvailableSections);
    const { REPORT_COMPONENT_DEFINITIONS } = useReportComponentDefinitions();
    const draft = useReportComponentStore(
        (state) => state.draftConfigurations[component?.id as string],
    ) as Partial<ReportComponentConfig>;
    const definition = REPORT_COMPONENT_DEFINITIONS[component.type];
    const capabilities = definition.capabilities;
    const reportAccess = useReportComponentAccess(component);
    const canModify = reportAccess.canUpdate && !component.locked;

    const dataSourceSelectionComplete =
        !capabilities.overview.dataSource ||
        Boolean(draft?.dataSet?.type && draft?.dataSet?.key) ||
        Boolean(draft?.dataSet?.type === DataSourceType.THIRD_PARTY && draft?.dataSet?.key);

    const hasAxesControls = Boolean(
        capabilities.xAxis?.metric ||
        capabilities.xAxis?.groupBy ||
        capabilities.yAxis?.metric ||
        capabilities.yAxis?.groupBy,
    );
    const hasAdditionalSettings = Object.values(capabilities.additionalSettings).some(Boolean);

    useEffect(() => {
        availableSectionsRef.current = initialAvailableSections;
        setOpenSections(createInitialOpenSections());
    }, [component.id, component.type]);

    const sections = useMemo(
        () => [
            { key: 'overview' as const, enabled: true, hidden: false },
            {
                key: 'filters' as const,
                enabled: dataSourceSelectionComplete,
                hidden: !capabilities.overview.filters,
            },
            {
                key: 'axes' as const,
                enabled: dataSourceSelectionComplete,
                hidden: !hasAxesControls,
            },
            {
                key: 'formatting' as const,
                enabled: dataSourceSelectionComplete,
                hidden: !hasChartFormattingControls(component.type),
            },
            {
                key: 'additionalSettings' as const,
                enabled: dataSourceSelectionComplete,
                hidden: !hasAdditionalSettings,
            },
        ],
        [
            capabilities.overview.filters,
            component.type,
            dataSourceSelectionComplete,
            hasAdditionalSettings,
            hasAxesControls,
        ],
    );

    useEffect(() => {
        const previousAvailableSections = availableSectionsRef.current;
        const nextAvailableSections = sections.reduce(
            (result, section) => ({
                ...result,
                [section.key]: !section.hidden && section.enabled,
            }),
            {} as Record<SettingsSectionKey, boolean>,
        );

        setOpenSections((current) => {
            const next = sections.reduce((result, section) => {
                const available = nextAvailableSections[section.key];
                const wasAvailable = previousAvailableSections[section.key];

                if (!available) {
                    return {
                        ...result,
                        [section.key]: false,
                    };
                }

                if (available && !wasAvailable) {
                    return {
                        ...result,
                        [section.key]: true,
                    };
                }

                return result;
            }, current);

            const changed = sections.some((section) => next[section.key] !== current[section.key]);

            return changed ? next : current;
        });

        availableSectionsRef.current = nextAvailableSections;
    }, [sections]);

    const toggleSection = (sectionKey: SettingsSectionKey) => {
        setOpenSections((current) => ({
            ...current,
            [sectionKey]: !current[sectionKey],
        }));
    };

    return (
        <FullHeightFlexColumn>
            <Header onRequestClose={onRequestClose} />
            <Tabs
                size='small'
                defaultActiveKey='edit'
                items={[
                    {
                        key: 'edit',
                        label: (
                            <span style={{ alignItems: 'center', display: 'inline-flex', gap: '0.25rem' }}>
                                <TbEdit />
                                Edit
                            </span>
                        ),
                        children: (
                            <FullHeightFlexColumn>
                                <FlexContentRegion overflowY='auto'>
                                    <Form disabled={!canModify} layout='vertical'>
                                        <SettingsSection
                                            title='Overview'
                                            description='Configure the component type, dataset, category, and reporting period.'
                                            icon={<TbInfoCircle />}
                                            sectionKey='overview'
                                            open={openSections.overview}
                                            onToggle={toggleSection}
                                            first
                                        >
                                            <Overview component={component} />
                                        </SettingsSection>
                                        {capabilities.overview.filters && (
                                            <SettingsSection
                                                title='Filters'
                                                description='Narrow the dataset with governed filter criteria.'
                                                icon={<ListFilter size='0.875rem' />}
                                                sectionKey='filters'
                                                open={openSections.filters}
                                                onToggle={toggleSection}
                                                enabled={dataSourceSelectionComplete}
                                            >
                                                <Filters component={component} />
                                            </SettingsSection>
                                        )}
                                        {hasAxesControls && (
                                            <SettingsSection
                                                title='Axes'
                                                description='Choose the metrics and dimensions used by the visualization.'
                                                icon={<TbChartDots2 />}
                                                sectionKey='axes'
                                                open={openSections.axes}
                                                onToggle={toggleSection}
                                                enabled={dataSourceSelectionComplete}
                                            >
                                                <Axes component={component} />
                                            </SettingsSection>
                                        )}
                                        {hasChartFormattingControls(component.type) && (
                                            <SettingsSection
                                                title='Formatting'
                                                description='Customize the component presentation and visual styling.'
                                                icon={<TbPalette />}
                                                sectionKey='formatting'
                                                open={openSections.formatting}
                                                onToggle={toggleSection}
                                                enabled={dataSourceSelectionComplete}
                                            >
                                                <Formatting component={component} />
                                            </SettingsSection>
                                        )}
                                        {hasAdditionalSettings && (
                                            <SettingsSection
                                                title='Additional Settings'
                                                description='Control component-specific display behavior.'
                                                icon={<TbSettings />}
                                                sectionKey='additionalSettings'
                                                open={openSections.additionalSettings}
                                                onToggle={toggleSection}
                                                enabled={dataSourceSelectionComplete}
                                            >
                                                <AdditionalSettings component={component} />
                                            </SettingsSection>
                                        )}
                                    </Form>
                                </FlexContentRegion>
                                <Footer component={component} onSave={onSave} />
                            </FullHeightFlexColumn>
                        ),
                    },
                    {
                        key: 'share',
                        label: (
                            <span style={{ alignItems: 'center', display: 'inline-flex', gap: '0.25rem' }}>
                                <TbShare />
                                Share
                            </span>
                        ),
                        children: <Share component={component} />,
                    },
                ]}
                style={{ flex: '1 1 0%', minHeight: 0, overflow: 'hidden' }}
                tabBarStyle={{ margin: 0, padding: '0 1rem' }}
            />
        </FullHeightFlexColumn>
    );
};

export default Settings;
